import { RootState, AppDispatch } from '@/store';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { clamp } from 'lodash';
import APP_CONFIG from '@/configs/appConfig';

const renderer = ProjectRenderer.getInstance();

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setProduct,
	effect: async ({ payload }, listenerApi) => {
		renderer.startProduct(payload.product);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectAttributeValue,
	effect: async ({ payload }, listenerApi) => {
		const { product, variations, selectedAttributeValues } =
			listenerApi.getState().creator;
		if (!product) return;

		if (payload.value.realizationTime)
			listenerApi.dispatch(
				creatorActions.updateCreator({
					realizationTime: payload.value.realizationTime,
				})
			);

		renderer.handleActions(payload.value.actions);

		const selectedValues = Object.values(selectedAttributeValues).filter(
			(value) => value !== null
		);

		const matchedVariation = variations.find((variation) => {
			return variation.attributeValues.every((attributeValue) =>
				selectedValues.some(
					(selectedValue) => selectedValue.id === attributeValue.id
				)
			);
		});

		if (matchedVariation)
			listenerApi.dispatch(creatorActions.setVariation(matchedVariation));
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setVariation,
	effect: async ({ payload }, listenerApi) => {
		const { length, options } = listenerApi.getState().creator;
		if (!payload) return;

		const clamped = clamp(length, APP_CONFIG.LENGTH.MIN, options.maxLength);
		listenerApi.dispatch(creatorActions.setLength(clamped));
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setLength,
	effect: async ({ payload }, listenerApi) => {
		const { options, siphons } = listenerApi.getState().creator;

		const length =
			clamp(payload, APP_CONFIG.LENGTH.MIN, options.maxLength) / 1000;

		const max = Math.ceil(APP_CONFIG.SIPHONS.MAX_PER_1000_MM * length);

		const excessNumber = max - siphons.length;

		if (excessNumber < 0)
			listenerApi.dispatch(creatorActions.setSiphonsQuantity(max));

		renderer.productBuilder.length = length;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorSiphon,
	effect: async ({ payload }, listenerApi) => {},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setSiphonsQuantity,
	effect: async ({ payload }, listenerApi) => {
		renderer.siphonsManager.syncWithCreatorState(
			listenerApi.getState().creator.siphons
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setSiphonPosX,
	effect: async ({ payload }, listenerApi) => {
		const { options } = listenerApi.getState().creator;

		renderer.siphonsManager.setSiphonPosX(
			payload.id,
			clamp(payload.posX, options.minSiphonsXpos, options.maxSiphonsXpos) / 1000
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setIsAutoSiphonsPositioning,
	effect: async ({ payload }, listenerApi) => {
		renderer.siphonsManager.isAutoSiphonsPositioning = payload;
	},
});
